import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Rocket, Globe, Lock } from 'lucide-react';

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  const team = [
    {
      name: "Adarsh Jain",
      role: "Co-Founder, CEO",
      university: "IIT BHU (Electrical)",
      bio: [
        "20+ yrs of building & scaling tech products",
        "Led B2B sales at HT, Thirdwatch AI (acq. by Razorpay), Octro"
      ],
      imageUrl: "https://customer-assets.emergentagent.com/job_better-site-13/artifacts/q007yd20_adarsh%20pic.png"
    },
    {
      name: "Piyush Shandilya",
      role: "Co-Founder, CTO",
      university: "IIT Bombay (Electrical)",
      bio: [
        "16+ yrs of tech leadership at HDFC ERGO, Octro, Yatra"
      ],
      imageUrl: "https://customer-assets.emergentagent.com/job_better-site-13/artifacts/zgidzsx6_piyush%20pic.png"
    },
    {
      name: "Tushar Gupta",
      role: "Co-Founder",
      university: "NSUT (CS)",
      bio: [
        "Data Scientist at Octro",
        "10X Coder (Hardware + Software)"
      ],
      imageUrl: "https://customer-assets.emergentagent.com/job_better-site-13/artifacts/iv78lgg8_Tushar.png"
    }
  ];

  const whyUs = [
    { icon: Rocket, title: "Enterprise Scalability", desc: "Engineered to handle thousands of concurrent streams across massive geographical footprints." },
    { icon: Globe, title: "India-First, Global-Ready", desc: "Optimized for diverse lighting and varied store layouts common in emerging markets." },
    { icon: Lock, title: "Privacy by Design", desc: "Built to exceed global security standards, ensuring customer and staff privacy through metadata processing." }
  ];

  return (
    <div data-testid="about-page" className="bg-white">
      {/* Mission */}
      <section className="py-16 bg-secondary/30">
        <div className="container-custom text-center">
          <AnimatedSection>
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-4 border border-primary/20">
              Our Mission
            </span>
            <h1 className="text-4xl lg:text-6xl font-extrabold font-heading text-foreground mb-6 max-w-4xl mx-auto leading-tight">
              The Vision AI layer for <span className="text-gradient">Physical Business.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We are building the intelligence stack that turns standard video streams into real-time operational metrics for the world's leading retailers.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Why ProAct AI */}
      <section className="section-padding border-b border-border">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="space-y-6">
                <h2 className="text-3xl font-bold font-heading text-foreground">Why ProAct AI?</h2>
                <p className="text-muted-foreground">Designed for high-trust enterprise retail environments.</p>
                <div className="space-y-6">
                  {whyUs.map((item, i) => (
                    <div key={i} className="flex gap-4" data-testid={`why-us-${i}`}>
                      <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center text-primary shrink-0">
                        <item.icon size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">{item.title}</h4>
                        <p className="text-muted-foreground text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-primary/10 to-accent/10 rounded-3xl blur-2xl opacity-50"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border">
                  <img 
                    src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=1200" 
                    className="w-full h-[400px] object-cover" 
                    alt="ProAct AI Vision Analytics" 
                  />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Company Video */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl font-bold font-heading text-foreground mb-4">Our Story</h2>
              <p className="text-muted-foreground text-lg">
                Watch how we're transforming retail operations with Vision AI.
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={0.2}>
            <div className="relative max-w-4xl mx-auto">
              <div className="absolute -inset-4 bg-gradient-to-tr from-cyan-400/20 via-purple-500/10 to-pink-500/20 rounded-3xl blur-2xl opacity-50"></div>
              <div className="relative aspect-video w-full rounded-2xl overflow-hidden shadow-2xl border border-border bg-foreground">
                <iframe 
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/omtUUQtm-pU?rel=0" 
                  title="ProAct AI - Our Story"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="section-padding bg-secondary/30">
        <div className="container-custom">
          <AnimatedSection>
            <div className="text-center mb-12 max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold font-heading text-foreground mb-4">Leadership Team</h2>
              <p className="text-lg text-muted-foreground">
                2nd time founders and AI-experts shipping <span className="text-primary font-bold">15+ AI/ML products</span> driving <span className="text-primary font-bold">&gt;$50M in revenue</span>
              </p>
            </div>
          </AnimatedSection>
           
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div 
                  data-testid={`team-member-${i}`}
                  className="bg-white rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                >
                  <div className="aspect-square relative overflow-hidden bg-secondary">
                    <img 
                      src={member.imageUrl} 
                      alt={member.name} 
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" 
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="font-bold text-xl text-foreground mb-1 font-heading">{member.name}</h4>
                    <p className="text-xs text-primary font-semibold uppercase tracking-wider mb-3">{member.role}</p>
                    
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground flex items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mr-2"></span>
                        {member.university}
                      </p>
                      {member.bio.map((point, idx) => (
                        <p key={idx} className="text-xs text-muted-foreground flex items-start">
                          <span className="w-1 h-1 rounded-full bg-border mt-1.5 mr-2 shrink-0"></span>
                          {point}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
